import { useCallback } from 'react';

import { useCreateOAuthUser, useCreateUser } from 'apps/shared/scripts/graphql';
import { useFormAsync } from 'apps/shared/scripts/hooks';

import { useLoginUser } from '../../hooks';

import SignupForm, { FormValues, SignupFormProps } from './SignupForm';
import { toInput, toOAuthInput } from './utils';

interface OwnProps {
  onSuccess?: () => void;
}

type FormProps = Pick<
  SignupFormProps,
  'onLogin' | 'modal' | 'onboarding'
>;

type Props = OwnProps & FormProps;

const SignupContainer = ({ onSuccess, ...props }: Props) => {
  const { mutate: createUser } = useCreateUser();
  const { mutate: createOAuthUser } = useCreateOAuthUser();
  const { login } = useLoginUser();

  const { isLoading, onStart, onFail, onSuccess: onSuccessAsync } = useFormAsync();

  const handleSignup = useCallback(
    async (data: FormValues) => {
      onStart();
      const input = toInput(data);
      const { result, errors } = await createUser({ input });
      if (!result || errors.length > 0) {
        onFail();
        throw new Error(errors[0].message);
      }
      await login(data.email, data.password);
      onSuccessAsync();
      if (onSuccess) {
        onSuccess();
      }
    },
    [createUser, login, onFail, onStart, onSuccess, onSuccessAsync],
  );

  const handleOAuthSignup = useCallback(
    async (idToken: string) => {
      onStart();
      const input = toOAuthInput(idToken);
      const { result, errors } = await createOAuthUser({ input });
      if (!result || errors.length > 0) {
        onFail();
        throw new Error(errors[0].message);
      }
      if (onSuccess) {
        onSuccess();
      }
    },
    [createOAuthUser, onFail, onStart, onSuccess],
  );

  return (
    <SignupForm
      {...props}
      handleOAuthSignup={handleOAuthSignup}
      handleSignup={handleSignup}
      isLoading={isLoading}
    />
  );
};

SignupContainer.displayName = 'SignupContainer';

export default SignupContainer;
